import { Component, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, Event as NavigationEvent, NavigationEnd } from '@angular/router';
import { takeUntil } from 'rxjs';
import { AuthService } from 'src/app/service/auth.service';
import { Destroy } from 'src/app/service/destroy';
import { StorageService } from 'src/app/service/storage.service';
import { TranslationService } from 'src/app/service/translation.service';
import { LanguageService } from 'src/app/service/language.service';
import { CommonService } from 'src/app/service/common.service';

@Component({
    selector: 'app-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['./side-nav.component.css'],
    viewProviders: [Destroy],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
    imports: [CommonModule],
})
export class SideNavComponent implements OnInit {
  private commonService = inject(CommonService);
  sidebarOpen = toSignal(this.commonService.sidebarOpen$, { initialValue: true });
  isMobile = toSignal(this.commonService.isMobile$, { initialValue: false });
  currentLanguage = signal('en');
  viewMode = signal<'table' | 'card'>('table');

  navItems: any[] = [];
  userData: any;
  currentUrl: string = '';
  
  constructor(private authService: AuthService, private storage: StorageService, public destroy$: Destroy, private router: Router, private route: ActivatedRoute, public translationService: TranslationService, public languageService: LanguageService){
    this.currentUrl = this.router.url;
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: NavigationEvent) => {
    if(event instanceof NavigationEnd){
      this.currentUrl = event.urlAfterRedirects || event.url;
      if (this.commonService.isMobile$.value) {
        this.commonService.sidebarOpen$.next(false);
      }
    }
   })
  }

  ngOnInit(): void {
    this.currentLanguage.set(this.languageService.current || 'en');
    this.viewMode.set(this.commonService.viewMode$.value);

    this.translationService.currentLang$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => {
        this.currentLanguage.set(language || 'en');
      });

    this.commonService.viewMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => {
        this.viewMode.set(mode);
      });

    // Load sidebar immediately
    this.loadSideBar();

    // Update nav labels when language changes or when translations are ready
    this.translationService.currentLang$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.translateNavItems());

    this.translationService.translationsReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ready) => {
        if (ready) this.translateNavItems();
      });
  }

  getUserData() {
    this.storage.getItem('local', 'authData').pipe(takeUntil(this.destroy$)).subscribe((userData: any) => {
      this.userData = userData;
      this.applyStoreFrontBannersNavFilter();
      this.translateNavItems();
    })
  }

  private loadSideBar(): void {
    this.authService.getSideBar().pipe(takeUntil(this.destroy$)).subscribe((response: any) => {
      // parse the response into navItems
      
      // Handle different response structures
      if (Array.isArray(response)) {
        this.navItems = response;
      } else if (response?.data && Array.isArray(response.data)) {
        this.navItems = response.data;
      } else if (response?.navs && Array.isArray(response.navs)) {
        this.navItems = response.navs;
      } else if (response) {
        // If response itself is the nav array or object, try to extract it
        this.navItems = typeof response === 'object' ? Object.values(response).filter((item: any) => item?.routerLink) : [];
      } else {
        this.navItems = [];
      }
      
      // If navItems is still empty, use fallback
      if (!this.navItems || this.navItems.length === 0) {
        this.navItems = this.getFallbackNavItems();
      } else {
        this.navItems = this.navItems.map((item: any) => {
          if (item.routerLink === '/discounts') {
            return { ...item, iconClass: 'sidebar-discounts' };
          }
          if (item.routerLink === '/faqs') {
            return { ...item, iconClass: 'sidebar-faqs' };
          }
          return item;
        });
      }

      this.getUserData();
    },
    (error) => {
      this.navItems = this.getFallbackNavItems();
      this.getUserData();
    });
  }

  private applyStoreFrontBannersNavFilter(): void {
    if (!Array.isArray(this.navItems)) return;
    if (this.authService.shouldShowStoreFrontBanners(this.userData)) return;
    this.navItems = this.navItems.filter(
      (item: any) =>
        item.routerLink !== '/store-front-banners' && item.id !== 18 &&
        item.routerLink !== '/appearance' && item.id !== 20
    );
  }

  private getFallbackNavItems(): any[] {
    // Fallback navigation items if API fails or returns empty
    return [
      {
        id: 1,
        name: 'Dashboard',
        routerLink: '/dashboard',
        iconClass: 'sidebar-dashboard',
      },
      {
        id: 11,
        name: 'All Orders',
        routerLink: '/orders',
        iconClass: 'sidebar-orders',
      },
      {
        id: 12,
        name: 'Menu',
        routerLink: '/menu',
        iconClass: 'sidebar-menu',
      },
      {
        id: 13,
        name: 'Products',
        routerLink: '/products',
        iconClass: 'sidebar-products',
      },
      {
        id: 14,
        name: 'Discounts',
        routerLink: '/discounts',
        iconClass: 'sidebar-discounts',
      },
      {
        id: 15,
        name: 'Ratings & Reviews',
        routerLink: '/ratings-reviews',
        iconClass: 'sidebar-ratings-reviews',
      },
      {
        id: 16,
        name: 'My Earnings',
        routerLink: '/my-earnings',
        iconClass: 'sidebar-earnings',
      },
      {
        id: 19,
        name: 'FAQs',
        routerLink: '/faqs',
        iconClass: 'sidebar-faqs',
      },
      {
        id: 17,
        name: 'My Profile',
        routerLink: '/store-profile',
        iconClass: 'sidebar-profile',
      },
      {
        id: 18,
        name: 'Store Front Banners',
        routerLink: '/store-front-banners',
        iconClass: 'sidebar-profile',
      },
      {
        id: 20,
        name: 'Appearance',
        routerLink: '/appearance',
        iconClass: 'sidebar-profile',
      }
    ];
  }

  private translateNavItems(): void {
    // Only translate if navItems is an array with items
    if (!Array.isArray(this.navItems) || this.navItems.length === 0) return;

    // Create a translation key mapping for nav item names
    const translationMap: { [key: string]: string } = {
      'Dashboard': 'navigation.dashboard',
      'Quotations': 'navigation.quotations',
      'Chats': 'navigation.chat',
      'My Earnings': 'navigation.earnings',
      'My Reviews': 'navigation.reviews',
      'Job History': 'navigation.jobHistory',
      'Cancelled Jobs': 'navigation.cancelledJobs',
      'My Documents': 'navigation.documents',
      'Employee List': 'navigation.employees',
      'My Profile': 'navigation.myProfile',
      'All Orders': 'navigation.allOrders',
      'Menu': 'navigation.menu',
      'Products': 'navigation.products',
      'Discounts': 'navigation.discount',
      'Ratings & Reviews': 'navigation.ratingsReviews',
      'Store Profile': 'navigation.storeProfile',
      'Store Front Banners': 'navigation.storeFrontBanners',
      'Appearance': 'navigation.appearance',
      'FAQs': 'navigation.faqs',
      'Legal': 'navigation.legal'
    };
    // Translate nav items - add displayName to each item
    this.navItems.forEach((item: any) => {
      const key = translationMap[item.name];
      item.displayName = key ? this.translationService.instant(key, item.name) : item.name;
    });
  }

  routeTo(route: string){
    this.router.navigate([`${route}`]);
  }

  selectLanguage(language: string): void {
    if (this.currentLanguage() === language) {
      return;
    }
    this.languageService.setLanguage(language);
    this.translationService.setLanguage(language);
    this.currentLanguage.set(language);
  }

  setViewMode(mode: 'table' | 'card'): void {
    this.commonService.setViewMode(mode);
  }

  isNavActive(routerLink: string): boolean {
    const url = (this.currentUrl || '').split('?')[0];
    if (routerLink === '/dashboard') {
      return url === '/dashboard' || url === '/';
    }
    return url === routerLink || url.startsWith(`${routerLink}/`);
  }

  get isArabic(): boolean {
    return this.languageService.current === 'ar';
  }

}
